import React, { forwardRef, useState } from 'react';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { ICONS } from 'constants/icons';
import { intlMessage } from 'constants/propTypes';

import { bem } from 'helpers/class-name';

import './AutocompleteSearch.scss';

const { element } = bem('autocomplete-search');

const AutocompleteSearch = forwardRef(({ onBlur, onChange, options, placeholder }, ref) => {
  const intl = useIntl();

  const [searchValue, setSearchValue] = useState('');
  const [suggestion, setSuggestion] = useState('');

  const handleChange = event => {
    const newSearchValue = event.target.value;
    setSearchValue(newSearchValue);
    onChange(newSearchValue);
  };

  const handleKeyDown = event => {
    if (event.key.toUpperCase() === 'TAB') {
      event.preventDefault();
    }
  };

  const handleKeyUp = event => {
    if (event.key.toUpperCase() === 'TAB') {
      const newSearchValue = suggestion;
      setSearchValue(newSearchValue);
      onChange(newSearchValue);
      setSuggestion('');
      return;
    }

    const searchInput = event.target.value;

    if (!searchInput.trim().length) {
      setSuggestion('');
      return;
    }

    options.some(searchOption => {
      const regex = new RegExp(`^${searchInput}`, 'i');
      if (regex.test(searchOption)) {
        if (searchOption.length === searchInput.length) {
          // If the searchOption is equal to the the searchInput, return false in order to continue
          // searching for the next available option
          return false;
        }
        setSuggestion(searchInput + searchOption.slice(searchInput.length));
        return true;
      }
      setSuggestion('');
      return false;
    });
  };

  return (
    <div className={element('container')}>
      <Isvg className={element('icon')} src={ICONS.searchGlass} />
      <input
        className={element('input')}
        onBlur={onBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        placeholder={!placeholder ? '' : intl.formatMessage(placeholder)}
        ref={ref}
        value={searchValue}
        aria-label="Search input"
      />
      <input
        className={element('suggestion')}
        value={suggestion}
        readOnly
        aria-label="Search suggestion"
        aria-hidden="true"
      />
    </div>
  );
});

AutocompleteSearch.propTypes = {
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.string),
  placeholder: intlMessage,
};

export default AutocompleteSearch;
